/* oxlint-disable no-param-reassign */

'use client';

import html2canvas from 'html2canvas';
import { useTranslations } from 'next-intl';
import { ReactNode, useEffect, useRef, useState } from 'react';

import { FormattedMessage } from '@flipster/i18n';
import { Modal } from '@flipster/react/design-system/modal';
import { RadioButton } from '@flipster/react/design-system/radio-button';
import { snackbar } from '@flipster/react/design-system/snackbar';
import { SpinnerBox } from '@flipster/react/design-system/spinner';
import { css } from '@flipster/styled-system/css';
import { hstack } from '@flipster/styled-system/patterns';
import { downloadImageFile } from '@flipster/utils/file';
import segment from '@flipster/utils/segment';
import { PnlClickParams, PositionEvent } from '@flipster/utils/segment/events/position';

import { imgToSpan } from '../../utils/dom-converter';
import { useAuth } from '../auth/state';
import { useReferralCode } from '../referral-code/state';
import { useCheckSpotOnly } from '../regulation';
import { useRawSnapshotSpec } from '../snapshot/use-snapshot-spec';
import { getFlipsterUrl } from '../urls';
import { useUserTier } from '../user-tier/state';
import { useCopy } from '../webview/clipboard';
import { PnlShareCard, PnlSharePosition, cardSize } from './card';
import { shouldShowReferralSection } from './utils';

type RadioValueType = 'roi' | 'pnl';

interface PnlShareModalProps {
  position: PnlSharePosition;
  isOpen: boolean;
  onClose: () => void;
  onExit: () => void;
}
export const PnlShareModal = ({ position, isOpen, onClose, onExit }: PnlShareModalProps) => {
  const transShare = useTranslations('Share.r250807');
  const transAssets = useTranslations('Assets.r250401');
  const transCommon = useTranslations('Common.r250401');
  const transReferral = useTranslations('Referral.r250401');
  const imageRef = useRef<HTMLDivElement>(null);

  const [selected, setSelected] = useState<RadioValueType>('roi');
  const radioItems: { label: ReactNode; value: RadioValueType }[] = [
    { label: <FormattedMessage id='Common.r251127.Roi' />, value: 'roi' },
    { label: transAssets('Pnl'), value: 'pnl' },
  ];

  const { isLoggedIn, isCookieLoggedIn } = useAuth();
  const { userTier, isTierLoading } = useUserTier();
  // NOTE: useUserTier의 쿼리는 로그인 상태에서만 enabled라서, 비로그인 시 isTierLoading이 계속 true로 유지됨
  const isTierPending = isCookieLoggedIn && isTierLoading;
  const copy = useCopy(transReferral('Link_copied_toast'));
  const { instrumentsKeyBySymbol } = useRawSnapshotSpec();
  const isSpotOnly = useCheckSpotOnly();

  const { referralCode } = useReferralCode({ enabled: isLoggedIn && isOpen });
  const copyUrl = `${getFlipsterUrl()}/signup?referral_code=${referralCode ?? ''}&referral_type=copy`;

  const getTrackerParams = (): PnlClickParams | null => {
    if (!position) return null;
    const symbolInfo = instrumentsKeyBySymbol?.[position.symbol];
    if (!symbolInfo) return null;
    return {
      currency: symbolInfo.displayShortName,
      symbol: position.symbol,
      trade_type: position.type,
      unrealized_pnl: Number(position.pnl),
      unrealized_pnl_percentage: Number(position.pnlPercentage),
      realized_pnl: null,
      realized_pnl_percentage: null,
      side: position.type === 'perpetual' ? (position.side === 'Long' ? 'buy' : 'sell') : null,
      profit_boost_mode: position.type === 'perpetual' && position.profitBoostMode,
    };
  };

  const handleDownloadImage = async () => {
    if (!imageRef.current) return;

    try {
      const canvas = await html2canvas(imageRef.current, {
        scale: 3,
        useCORS: true,
        // 카드 rounded 바깥 영역이 흰색으로 채워지지 않도록 (html2canvas 기본값이 #ffffff)
        backgroundColor: null,
        onclone: (doc, clonedEl) => {
          const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
          // NOTE(namhyunkim): Safari에서 img src svg symbol 크기가 잘못 조정되는 문제 해결, img 태그를 span으로 교체하고 backgroundImage로 설정
          if (isSafari) {
            const clonedElements = (clonedEl as HTMLDivElement).querySelectorAll('[data-role="symbol-icon"]');

            clonedElements.forEach((clonedElement) => {
              if (clonedElement.tagName === 'IMG') {
                const imgElement = clonedElement as HTMLImageElement;
                const spanElement = imgToSpan(imgElement, doc);
                imgElement.parentNode?.replaceChild(spanElement, imgElement);
              }
            });
          }
        },
      });

      const dataUrl = canvas.toDataURL('image/png');
      downloadImageFile('pnl-share', dataUrl);
      snackbar.success({ message: transReferral('Image_saved_toast') });
    } catch {
      snackbar.error({ message: transReferral('Image_save_failed_toast') });
    }

    const params = getTrackerParams();
    if (params) {
      segment.track(PositionEvent.PnLShareDownloadClick, params);
    }
  };

  const onClickCopy = () => {
    copy(copyUrl);

    const params = getTrackerParams();
    if (params) segment.track(PositionEvent.PnLShareCopyClick, params);
  };

  useEffect(() => {
    if (isOpen) segment.track(PositionEvent.PnLSharePageView);
  }, [isOpen]);

  return (
    <Modal.Root isOpen={isOpen} size='small' onClose={onClose} onExit={onExit}>
      <Modal.Header>
        <Modal.Title
          label={transShare(position.status === 'open' ? 'Unrealized_profit_and_loss' : 'Realized_profit_and_loss')}
        />
      </Modal.Header>
      <Modal.Content>
        <section
          className={css({
            w: cardSize.width,
            mx: 'auto',
          })}
        >
          <div
            className={css({
              mb: 24,
            })}
            ref={imageRef}
          >
            {/* VIP 티어를 모르는 상태로 카드를 그리면 배지 없는 이미지가 캡처될 수 있어 로딩 중에는 스피너를 노출 */}
            {isTierPending ? (
              <div
                className={css({
                  w: cardSize.width,
                  h: cardSize.height,
                  rounded: 20,
                  backgroundColor: 'background.default.level-0',
                })}
              >
                <SpinnerBox />
              </div>
            ) : (
              <PnlShareCard
                position={position}
                selected={selected}
                copyUrl={copyUrl}
                referralCode={referralCode}
                radioItems={radioItems}
                vipTier={userTier?.vipTier}
                tierStatus={userTier?.status}
              />
            )}
          </div>
          <fieldset className={hstack({ gap: 24, justifyContent: 'center' })}>
            {radioItems.map((item) => (
              <RadioButton
                key={item.value}
                name='pnl-share'
                value={item.value}
                checked={selected === item.value}
                onChange={() => setSelected(item.value)}
                css={{ alignItems: 'center' }}
              >
                {item.label}
              </RadioButton>
            ))}
          </fieldset>
        </section>
      </Modal.Content>
      <Modal.Footer>
        <Modal.Button
          color='neutral'
          hierarchy='secondary'
          size='large'
          withSpinner
          disabled={isTierPending}
          label={transCommon('Download')}
          onClick={handleDownloadImage}
        />
        {shouldShowReferralSection(isSpotOnly) && (
          <Modal.Button size='large' label={transCommon('Copy_link')} onClick={onClickCopy} />
        )}
      </Modal.Footer>
    </Modal.Root>
  );
};
